import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  type: ToastType;
  message: string;
  onClose: () => void;
  duration?: number;
}

const typeConfig: Record<ToastType, { bg: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-green-50',
    text: 'text-green-800',
    icon: '✓',
  },
  error: {
    bg: 'bg-red-50',
    text: 'text-red-800',
    icon: '✕',
  },
  info: {
    bg: 'bg-blue-50',
    text: 'text-blue-800',
    icon: 'ℹ',
  },
  warning: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    icon: '⚠',
  },
};

/**
 * Toast notification component with accessibility
 * Auto-dismisses after duration (default: 5000ms)
 */
export const Toast: React.FC<ToastProps> = ({ type, message, onClose, duration = 5000 }) => {
  const config = typeConfig[type];

  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div
      className={`fixed bottom-4 right-4 max-w-md ${config.bg} ${config.text} p-4 rounded-lg shadow-lg border border-current border-opacity-20 flex items-start gap-3 animate-fadeIn`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="text-lg font-bold flex-shrink-0 mt-0.5" aria-hidden="true">
        {config.icon}
      </span>
      <p className="text-sm font-medium flex-1">{message}</p>
      <button
        onClick={onClose}
        className={`flex-shrink-0 text-opacity-70 hover:text-opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded p-1`}
        aria-label="Dismiss notification"
        type="button"
      >
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

Toast.displayName = 'Toast';
